import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import api from '../../api';
import Swal from 'sweetalert2';
import Select from 'react-select';
import {
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaEye,
  FaDownload,
  FaArrowLeft,
  FaBox,
  FaBoxOpen,
  FaUndo,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
  FaWarehouse,
  FaTruck,
  FaFileAlt,
  FaCalendarAlt,
  FaUser,
  FaChevronDown,
  FaChevronUp,
  FaFilter,
  FaCog,
  FaTimes,
  FaClipboardList
} from 'react-icons/fa';

const TilesReturn = () => {
  const { user } = useContext(AuthContext);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const [returns, setReturns] = useState([]);
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [selectedDeliveryOrder, setSelectedDeliveryOrder] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAdvancedFiltersModal, setShowAdvancedFiltersModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [filteredDeliveryOrders, setFilteredDeliveryOrders] = useState([]);
  const [allDeliveryOrders, setAllDeliveryOrders] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    originalDeliveryOrderId: '',
    items: [],
    stockItems: [],
    location: '',
    date: new Date().toISOString().split('T')[0],
    remarks: ''
  });
  

  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  // Advanced filters for delivery orders
  const [advancedFilters, setAdvancedFilters] = useState({
    clientName: '',
    deliveryOrderId: '',
    dateFrom: '',
    dateTo: '',
    location: '',
    status: '',
    minAmount: '',
    maxAmount: ''
  });
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (user) {
      fetchReturns();
      fetchDeliveryOrders();
      fetchLocations();
    }
  }, [user, page, searchTerm, filterStatus, dateRange]);

  const fetchReturns = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: searchTerm,
        startDate: dateRange.start,
        endDate: dateRange.end
      });

      const response = await api.get(`/tiles-return?${queryParams}`);
      setReturns(response.data.data || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching returns:', error);
      setReturns([]);
      if (error.response?.status !== 404) {
        Swal.fire({
          icon: 'error',
          title: 'Loading Failed',
          text: 'Failed to load tiles returns. Please refresh the page.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 4000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryOrders = async () => {
    try {
      // Fetch completed delivery orders from the tiles-return endpoint
      const response = await api.get('/tiles-return/delivery-orders', {
        params: {
          userId: user?.user?._id // Pass user ID to filter by company
        }
      });
      const orders = response.data.data || response.data || [];
      
      console.log('✅ Fetched delivery orders for return:', orders.length);
      
      setAllDeliveryOrders(orders);
      setDeliveryOrders(orders);
      setFilteredDeliveryOrders(orders);
      
      // Show informative message if no orders found
      if (orders.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'No Completed Delivery Orders',
          html: `
            <div style="text-align: left;">
              <p>No completed tiles delivery orders available for your company.</p>
              <br/>
              <p><strong>To create a return:</strong></p>
              <ol style="margin-left: 20px;">
                <li>Go to <strong>Tiles Delivery Order</strong> page</li>
                <li>Find a delivery order from your company's locations</li>
                <li>Mark it as <strong>"Completed"</strong> with invoice number and remarks</li>
                <li>Then come back here to create a return</li>
              </ol>
            </div>
          `,
          confirmButtonText: 'Got it',
          confirmButtonColor: '#4f46e5'
        });
      }
    } catch (error) {
      console.error('Error fetching tiles delivery orders:', error);
      setDeliveryOrders([]);
      setAllDeliveryOrders([]);
      setFilteredDeliveryOrders([]);
      
      if (error.response?.status !== 404) {
        Swal.fire({
          icon: 'warning',
          title: 'Unable to Load Delivery Orders',
          text: error.response?.data?.message || 'Failed to fetch delivery orders. Please try again.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 4000
        });
      }
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations');
      // API returns locations directly as an array, not wrapped in data.data
      const allLocations = response.data || [];
      
      // Filter locations by user's company
      const companyLocations = user?.company ? 
        allLocations.filter(location => location.company === user.company) : 
        allLocations;
      
      setLocations(companyLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    }
  };

  // Apply advanced filters to delivery orders
  const applyAdvancedFilters = () => {
    let filtered = [...allDeliveryOrders];

    // Filter by client name
    if (advancedFilters.clientName) {
      filtered = filtered.filter(order => {
        const recipientName = order.clientName?.name || order.recipient?.name || order.recipient?.businessName || '';
        return recipientName.toLowerCase().includes(advancedFilters.clientName.toLowerCase());
      });
    }

    // Filter by delivery order ID
    if (advancedFilters.deliveryOrderId) {
      filtered = filtered.filter(order => 
        order.deliveryOrderId?.toLowerCase().includes(advancedFilters.deliveryOrderId.toLowerCase())
      );
    }

    // Filter by date range
    if (advancedFilters.dateFrom) {
      filtered = filtered.filter(order => 
        new Date(order.date) >= new Date(advancedFilters.dateFrom)
      );
    }

    if (advancedFilters.dateTo) {
      filtered = filtered.filter(order => 
        new Date(order.date) <= new Date(advancedFilters.dateTo)
      );
    }

    // Filter by location
    if (advancedFilters.location) {
      filtered = filtered.filter(order => 
        order.location?._id === advancedFilters.location
      );
    }

    // Filter by status
    if (advancedFilters.status) {
      filtered = filtered.filter(order => 
        order.status?.toLowerCase() === advancedFilters.status.toLowerCase()
      );
    }

    // Filter by amount range (if available)
    if (advancedFilters.minAmount) {
      filtered = filtered.filter(order => 
        (order.totalAmount || 0) >= parseFloat(advancedFilters.minAmount)
      );
    }

    if (advancedFilters.maxAmount) {
      filtered = filtered.filter(order => 
        (order.totalAmount || 0) <= parseFloat(advancedFilters.maxAmount)
      );
    }

    setFilteredDeliveryOrders(filtered);
    setDeliveryOrders(filtered);
  };

  // Reset advanced filters
  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      clientName: '',
      deliveryOrderId: '',
      dateFrom: '',
      dateTo: '',
      location: '',
      status: '',
      minAmount: '',
      maxAmount: ''
    });
    setFilteredDeliveryOrders(allDeliveryOrders);
    setDeliveryOrders(allDeliveryOrders);
  };

  // Handle filter change
  const handleAdvancedFilterChange = (field, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };



  const handleDeliveryOrderChange = (deliveryOrderId) => {
    const selectedOrder = Array.isArray(deliveryOrders) ? deliveryOrders.find(order => order._id === deliveryOrderId) : null;
    setSelectedDeliveryOrder(selectedOrder);
    
    // Initialize form items based on tiles delivery order items
    const initialItems = selectedOrder ? selectedOrder.items.map(item => {
      const piecesPerBox = item.piecesPerBox || item.tileId?.tilesPiecesPerBox || 1;
      const deliveredBoxes = (item.freshBoxes || 0) + (item.damagedBoxes || 0);
      const deliveredPieces = (item.freshPieces || 0) + (item.damagedPieces || 0);
      // Use equivalent pieces for available calculation (allows piece returns against box deliveries)
      const availableTotalPieces = item.unreturnedTotalPieces ?? (deliveredBoxes * piecesPerBox + deliveredPieces);
      const availableBoxes = Math.floor(availableTotalPieces / piecesPerBox);

      return {
        tileId: item.tileId._id || item.tileId,
        tileName: item.tileId.tilesName || item.tileId.name || item.Name || 'Unknown Tile',
        tileSize: item.tileId.tilesMM?.parameterName || item.tileId.size || 'N/A',
        piecesPerBox,
        // Original delivered quantities
        deliveredFreshBoxes: item.freshBoxes || 0,
        deliveredFreshPieces: item.freshPieces || 0,
        deliveredDamagedBoxes: item.damagedBoxes || 0,
        deliveredDamagedPieces: item.damagedPieces || 0,
        // Already returned quantities
        totalReturnedBoxes: item.totalReturnedBoxes || 0,
        totalReturnedPieces: item.totalReturnedPieces || 0,
        // Available for return (in equivalent pieces and boxes)
        availableBoxes,
        availableTotalPieces,
        // Step 1: Total to return
        returnTotalBoxes: 0,
        returnTotalPieces: 0,
        // Dynamic max pieces (updates as boxes are entered)
        maxReturnPieces: availableTotalPieces,
        // Step 2: Distribution (fresh + corner + full = total)
        returnedFreshBoxes: 0,
        returnedFreshPieces: 0,
        returnedCornerDamagedBoxes: 0,
        returnedCornerDamagedPieces: 0,
        returnedFullDamagedBoxes: 0,
        returnedFullDamagedPieces: 0
      };
    }) : [];

    // Initialize stock items from DO
    const initialStockItems = selectedOrder?.stockItems ? selectedOrder.stockItems.map(si => ({
      stockItemId: si.stockItemId?._id || si.stockItemId,
      stockItemName: si.stockItemId?.name || si.name || 'Unknown Item',
      stockItemCode: si.stockItemId?.id || '',
      unitOfMeasure: si.stockItemId?.unitOfMeasure || 'pcs',
      // Delivered quantities
      deliveredFreshQuantity: si.freshQuantity || 0,
      deliveredDamagedQuantity: si.damagedQuantity || 0,
      // Already returned
      totalReturnedQuantity: si.totalReturnedQuantity || 0,
      // Available for return
      availableQuantity: si.unreturnedQuantity ?? ((si.freshQuantity || 0) + (si.damagedQuantity || 0)),
      // Return quantities
      returnTotalQuantity: 0,
      returnedFreshQuantity: 0,
      returnedDamagedQuantity: 0
    })) : [];

    setFormData(prev => ({
      ...prev,
      originalDeliveryOrderId: deliveryOrderId,
      items: initialItems,
      stockItems: initialStockItems
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    const item = updatedItems[index];
    let numValue = Math.max(0, Number(value) || 0);
    const piecesPerBox = item.piecesPerBox || 1;

    if (field === 'returnTotalBoxes') {
      numValue = Math.min(numValue, item.availableBoxes);
      // Recalculate max pieces based on boxes entered
      const maxPieces = Math.max(0, item.availableTotalPieces - (numValue * piecesPerBox));
      // Clamp existing pieces if they now exceed the new max
      const clampedPieces = Math.min(item.returnTotalPieces, maxPieces);
      // When total changes, auto-set fresh to total (user distributes from there)
      updatedItems[index] = {
        ...item,
        returnTotalBoxes: numValue,
        returnedFreshBoxes: numValue,
        returnedCornerDamagedBoxes: 0,
        returnedFullDamagedBoxes: 0,
        maxReturnPieces: maxPieces,
        returnTotalPieces: clampedPieces,
        returnedFreshPieces: clampedPieces,
        returnedCornerDamagedPieces: 0,
        returnedFullDamagedPieces: 0
      };
    } else if (field === 'returnTotalPieces') {
      // Max pieces = availableTotalPieces - (returnTotalBoxes * piecesPerBox)
      const maxPieces = Math.max(0, item.availableTotalPieces - (item.returnTotalBoxes * piecesPerBox));
      numValue = Math.min(numValue, maxPieces);
      updatedItems[index] = {
        ...item,
        returnTotalPieces: numValue,
        maxReturnPieces: maxPieces,
        returnedFreshPieces: numValue,
        returnedCornerDamagedPieces: 0,
        returnedFullDamagedPieces: 0
      };
    } else if (field === 'returnedFreshBoxes' || field === 'returnedCornerDamagedBoxes' || field === 'returnedFullDamagedBoxes') {
      numValue = Math.min(numValue, item.returnTotalBoxes);
      const updated = { ...item, [field]: numValue };
      // Auto-adjust: remaining goes to fresh
      const cornerAndFull = (field === 'returnedCornerDamagedBoxes' ? numValue : updated.returnedCornerDamagedBoxes) +
                            (field === 'returnedFullDamagedBoxes' ? numValue : updated.returnedFullDamagedBoxes);
      updated.returnedFreshBoxes = Math.max(0, updated.returnTotalBoxes - cornerAndFull);
      if (field === 'returnedCornerDamagedBoxes') updated.returnedCornerDamagedBoxes = numValue;
      if (field === 'returnedFullDamagedBoxes') updated.returnedFullDamagedBoxes = numValue;
      // Clamp if corner+full exceed total
      if (cornerAndFull > updated.returnTotalBoxes) {
        updated[field] = Math.max(0, updated.returnTotalBoxes - (cornerAndFull - numValue));
      }
      updatedItems[index] = updated;
    } else if (field === 'returnedFreshPieces' || field === 'returnedCornerDamagedPieces' || field === 'returnedFullDamagedPieces') {
      numValue = Math.min(numValue, item.returnTotalPieces);
      const updated = { ...item, [field]: numValue };
      const cornerAndFull = (field === 'returnedCornerDamagedPieces' ? numValue : updated.returnedCornerDamagedPieces) +
                            (field === 'returnedFullDamagedPieces' ? numValue : updated.returnedFullDamagedPieces);
      updated.returnedFreshPieces = Math.max(0, updated.returnTotalPieces - cornerAndFull);
      if (field === 'returnedCornerDamagedPieces') updated.returnedCornerDamagedPieces = numValue;
      if (field === 'returnedFullDamagedPieces') updated.returnedFullDamagedPieces = numValue;
      if (cornerAndFull > updated.returnTotalPieces) {
        updated[field] = Math.max(0, updated.returnTotalPieces - (cornerAndFull - numValue));
      }
      updatedItems[index] = updated;
    }

    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const handleStockItemChange = (index, field, value) => {
    const updatedStockItems = [...formData.stockItems];
    const item = updatedStockItems[index];
    let numValue = Math.max(0, Number(value) || 0);

    if (field === 'returnTotalQuantity') {
      numValue = Math.min(numValue, item.availableQuantity);
      updatedStockItems[index] = {
        ...item,
        returnTotalQuantity: numValue,
        returnedFreshQuantity: numValue,
        returnedDamagedQuantity: 0
      };
    } else if (field === 'returnedDamagedQuantity') {
      numValue = Math.min(numValue, item.returnTotalQuantity);
      updatedStockItems[index] = {
        ...item,
        returnedDamagedQuantity: numValue,
        returnedFreshQuantity: Math.max(0, item.returnTotalQuantity - numValue)
      };
    } else if (field === 'returnedFreshQuantity') {
      numValue = Math.min(numValue, item.returnTotalQuantity);
      updatedStockItems[index] = {
        ...item,
        returnedFreshQuantity: numValue,
        returnedDamagedQuantity: Math.max(0, item.returnTotalQuantity - numValue)
      };
    }

    setFormData(prev => ({ ...prev, stockItems: updatedStockItems }));
  };

  const handleCreateReturn = async () => {
    try {
      setLoading(true);
      
      // Validate tile items: distribution must equal total
      const validationErrors = [];
      
      formData.items.forEach((item) => {
        if (item.returnTotalBoxes <= 0 && item.returnTotalPieces <= 0) return; // skip empty
        
        const boxSum = (item.returnedFreshBoxes || 0) + (item.returnedCornerDamagedBoxes || 0) + (item.returnedFullDamagedBoxes || 0);
        const pieceSum = (item.returnedFreshPieces || 0) + (item.returnedCornerDamagedPieces || 0) + (item.returnedFullDamagedPieces || 0);
        
        if (boxSum !== item.returnTotalBoxes) {
          validationErrors.push(`${item.tileName}: Box distribution (${boxSum}) ≠ total returned boxes (${item.returnTotalBoxes})`);
        }
        if (pieceSum !== item.returnTotalPieces) {
          validationErrors.push(`${item.tileName}: Piece distribution (${pieceSum}) ≠ total returned pieces (${item.returnTotalPieces})`);
        }
        if (item.returnTotalBoxes > item.availableBoxes) {
          validationErrors.push(`${item.tileName}: Return boxes (${item.returnTotalBoxes}) exceeds available (${item.availableBoxes})`);
        }
        // Validate using equivalent pieces: boxes*piecesPerBox + pieces must not exceed available total pieces
        const piecesPerBox = item.piecesPerBox || 1;
        const returnEquivPieces = item.returnTotalBoxes * piecesPerBox + item.returnTotalPieces;
        if (returnEquivPieces > item.availableTotalPieces) {
          validationErrors.push(`${item.tileName}: Total return (${item.returnTotalBoxes}B + ${item.returnTotalPieces}P = ${returnEquivPieces} pieces) exceeds available (${item.availableTotalPieces} pieces)`);
        }
      });

      // Validate stock items
      formData.stockItems.forEach((si) => {
        if (si.returnTotalQuantity <= 0) return;
        const sum = (si.returnedFreshQuantity || 0) + (si.returnedDamagedQuantity || 0);
        if (sum !== si.returnTotalQuantity) {
          validationErrors.push(`${si.stockItemName}: Distribution (${sum}) ≠ total returned (${si.returnTotalQuantity})`);
        }
        if (si.returnTotalQuantity > si.availableQuantity) {
          validationErrors.push(`${si.stockItemName}: Return qty (${si.returnTotalQuantity}) exceeds available (${si.availableQuantity})`);
        }
      });

      if (validationErrors.length > 0) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Failed',
          html: `<div style="text-align: left; max-height: 300px; overflow-y: auto;">
                  <p><strong>Please fix the following issues:</strong></p>
                  <ul style="margin-top: 10px;">
                    ${validationErrors.map(err => `<li style="margin-bottom: 8px;">${err}</li>`).join('')}
                  </ul>
                 </div>`,
          confirmButtonColor: '#ef4444',
          width: '600px'
        });
        setLoading(false);
        return;
      }
      
      // Filter items that have return quantities
      const validItems = formData.items.filter(item => item.returnTotalBoxes > 0 || item.returnTotalPieces > 0);
      const validStockItems = formData.stockItems.filter(si => si.returnTotalQuantity > 0);

      if (validItems.length === 0 && validStockItems.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Items Selected',
          text: 'Please specify quantities to return for at least one item.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
        setLoading(false);
        return;
      }

      // Confirmation dialog with summary
      const summaryItems = validItems.map(i => 
        `<b>${i.tileName}</b>: ${i.returnTotalBoxes}B/${i.returnTotalPieces}P (Fresh: ${i.returnedFreshBoxes}B/${i.returnedFreshPieces}P, Corner: ${i.returnedCornerDamagedBoxes}B/${i.returnedCornerDamagedPieces}P, Full: ${i.returnedFullDamagedBoxes}B/${i.returnedFullDamagedPieces}P)`
      );
      const summaryStockItems = validStockItems.map(si =>
        `<b>${si.stockItemName}</b>: ${si.returnTotalQuantity} (Fresh: ${si.returnedFreshQuantity}, Damaged: ${si.returnedDamagedQuantity})`
      );

      const confirmResult = await Swal.fire({
        title: 'Confirm Tiles Return',
        html: `<div style="text-align: left; max-height: 300px; overflow-y: auto;">
          ${validItems.length > 0 ? `<p class="font-bold mb-2">Tiles (${validItems.length}):</p><ul style="margin-left:16px;margin-bottom:12px;">${summaryItems.map(s => `<li style="margin-bottom:6px;font-size:13px;">${s}</li>`).join('')}</ul>` : ''}
          ${validStockItems.length > 0 ? `<p class="font-bold mb-2">Stock Items (${validStockItems.length}):</p><ul style="margin-left:16px;">${summaryStockItems.map(s => `<li style="margin-bottom:6px;font-size:13px;">${s}</li>`).join('')}</ul>` : ''}
        </div>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#ef4444',
        confirmButtonText: 'Yes, Create Return',
        cancelButtonText: 'Cancel',
        width: '650px'
      });

      if (!confirmResult.isConfirmed) {
        setLoading(false);
        return;
      }

      const returnData = {
        originalDeliveryOrderId: formData.originalDeliveryOrderId,
        date: formData.date,
        remarks: formData.remarks,
        items: validItems.map(item => ({
          tileId: item.tileId,
          totalReturnedBoxes: item.returnTotalBoxes,
          totalReturnedPieces: item.returnTotalPieces,
          returnedFreshBoxes: item.returnedFreshBoxes,
          returnedFreshPieces: item.returnedFreshPieces,
          returnedCornerDamagedBoxes: item.returnedCornerDamagedBoxes,
          returnedCornerDamagedPieces: item.returnedCornerDamagedPieces,
          returnedFullDamagedBoxes: item.returnedFullDamagedBoxes,
          returnedFullDamagedPieces: item.returnedFullDamagedPieces
        })),
        stockItems: validStockItems.map(si => ({
          stockItemId: si.stockItemId,
          totalReturnedQuantity: si.returnTotalQuantity,
          returnedFreshQuantity: si.returnedFreshQuantity,
          returnedDamagedQuantity: si.returnedDamagedQuantity
        })),
        createdBy: user?.user?._id
      };

      const response = await api.post('/tiles-return', returnData);
      
      Swal.fire({
        icon: 'success',
        title: 'Return Created Successfully!',
        text: `Tiles return created with ID: ${response.data.data?.returnId}`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
      
      setShowCreateModal(false);
      resetForm();
      fetchReturns();
    } catch (error) {
      console.error('Error creating return:', error);
      Swal.fire({
        icon: 'error',
        title: 'Creation Failed',
        text: error.response?.data?.message || 'Failed to create tiles return. Please try again.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      originalDeliveryOrderId: '',
      items: [],
      stockItems: [],
      location: '',
      date: new Date().toISOString().split('T')[0],
      remarks: ''
    });
    setSelectedDeliveryOrder(null);
  };

  const handleViewReturn = (returnItem) => {
    setSelectedReturn(returnItem);
    setShowViewModal(true);
  };

  const toggleItemExpansion = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'processed': return darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800';
      case 'pending': return darkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
      case 'rejected': return darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800';
      default: return darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      <div className="w-full mx-auto space-y-6">
        
        {/* Header Section */}
        <div className={`rounded-2xl shadow-xl border-2 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl shadow-2xl p-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                  <FaUndo className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-white mb-2">
                    Tiles Return Management
                  </h1>
                  <p className="text-white/90">Process returned tiles and update inventory</p>
                </div>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-white/20 text-white rounded-xl hover:bg-white/30 transition-all backdrop-blur-sm flex items-center gap-2 font-semibold"
              >
                <FaPlus className="w-5 h-5" />
                New Return
              </button>
            </div>
          </div>

          {/* Filters Section */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="relative">
                <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder="Search returns..."
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-200'}`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <input
                type="date"
                className={`px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200'}`}
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({...prev, start: e.target.value}))}
                placeholder="Start Date"
              />
              <input
                type="date"
                className={`px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200'}`}
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({...prev, end: e.target.value}))}
                placeholder="End Date"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setDateRange({ start: '', end: '' });
                    setFilterStatus('');
                  }}
                  className={`flex-1 px-4 py-3 rounded-xl flex items-center justify-center gap-2 font-medium transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                >
                  <FaTimes className="w-4 h-4" />
                  Reset
                </button>
                <button className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-2 font-medium transition-colors">
                  <FaDownload className="w-4 h-4" />
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Returns List Section */}
        <div className={`rounded-2xl shadow-xl border-2 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl shadow-2xl p-6">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                <FaClipboardList className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white">Returns List</h2>
                <p className="text-white/90 text-sm">View and manage all tiles returns</p>
              </div>
              {returns.length > 0 && (
                <div className="bg-white/20 px-4 py-2 rounded-xl backdrop-blur-sm">
                  <span className="text-white font-bold text-lg">{returns.length}</span>
                  <span className="text-white/90 text-sm ml-2">Returns</span>
                </div>
              )}
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent"></div>
                <span className={`ml-3 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading returns...</span>
              </div>
            ) : returns.length === 0 ? (
              <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <div className={`rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <FaUndo className={`w-10 h-10 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
                <h4 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>No Returns Found</h4>
                <p className="text-sm mb-4">
                  {searchTerm || dateRange.start || dateRange.end
                    ? 'No returns match your current filters. Try adjusting your search criteria.'
                    : 'You haven\'t created any tiles returns yet.'}
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2 mx-auto transition-colors"
                >
                  <FaPlus />
                  Create First Return
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <thead>
                    <tr className="bg-gradient-to-r from-indigo-600 to-purple-600">
                      <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                        Return ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                        Delivery Order
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                        Items
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                    {returns.map((returnItem, index) => (
                      <tr 
                        key={returnItem._id || index} 
                        className={`transition-colors ${darkMode ? (index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750') : (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')} ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-indigo-50'}`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {returnItem.returnId}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {returnItem.originalDeliveryOrderId?.doNumber || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {returnItem.originalDeliveryOrderId?.recipient?.name || 
                             returnItem.originalDeliveryOrderId?.recipient?.businessName ||
                             returnItem.originalDeliveryOrderId?.recipient?.dealerName ||
                             returnItem.originalDeliveryOrderId?.recipient?.companyName || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {returnItem.location?.name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {new Date(returnItem.date).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {returnItem.items?.length || 0} item(s)
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(returnItem.status)}`}>
                            {returnItem.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <button
                              onClick={() => handleViewReturn(returnItem)}
                              className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
                              title="View Details"
                            >
                              <FaEye className="w-4 h-4" />
                            </button>
                            {returnItem.inwardCreated && (
                              <div className={`px-2 py-1 rounded text-xs font-medium ${darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'}`}>
                                <FaCheckCircle className="w-3 h-3 inline mr-1" />
                                Processed
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`mt-4 pt-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(page * limit, returns.length)}</span> of{' '}
                      <span className="font-medium">{returns.length}</span> results
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className={`px-4 py-2 border-2 rounded-xl transition-colors font-medium ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700 disabled:bg-gray-800 disabled:text-gray-600' : 'border-gray-300 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400'}`}
                    >
                      Previous
                    </button>
                    <span className={`px-4 py-2 text-sm flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Page {page} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className={`px-4 py-2 border-2 rounded-xl transition-colors font-medium ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700 disabled:bg-gray-800 disabled:text-gray-600' : 'border-gray-300 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400'}`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Return Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className={`relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                    <FaFileAlt className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Create New Tiles Return</h3>
                    <p className="text-white/90 text-sm mt-1">Process returned tiles from delivery orders</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className={`border-2 rounded-xl p-4 mb-6 ${darkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-start gap-3">
                  <FaInfoCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  <div>
                    <h4 className={`font-bold mb-1 ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>Return Process</h4>
                    <p className={`text-sm ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      1. Select a delivery order → 2. Specify return quantities → 3. Optional stock conversion → 4. Submit
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Content */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className={`border-2 p-6 rounded-xl shadow-sm transition-colors duration-300 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'}`}>
                  <h4 className={`font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="w-1.5 h-6 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full"></div>
                    Basic Information
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Select Tiles Delivery Order *
                      </label>
                      <Select
                        placeholder="Choose a tiles delivery order..."
                        value={(() => {
                          if (!Array.isArray(deliveryOrders)) return null;
                          const selectedOrder = deliveryOrders.find(order => order._id === formData.originalDeliveryOrderId);
                          if (!selectedOrder) return null;
                          
                          const orderDate = selectedOrder.date || selectedOrder.createdAt;
                          const formattedDate = orderDate && !isNaN(new Date(orderDate).getTime()) 
                            ? new Date(orderDate).toLocaleDateString() 
                            : 'N/A';
                          
                          return {
                            value: formData.originalDeliveryOrderId,
                            label: `${selectedOrder.deliveryOrderId || selectedOrder.doNumber} - ${selectedOrder.clientName?.name || selectedOrder.recipient?.name || selectedOrder.recipient?.businessName || 'N/A'} (${formattedDate})`
                          };
                        })()}
                        onChange={(selectedOption) => handleDeliveryOrderChange(selectedOption?.value || '')}
                        options={Array.isArray(deliveryOrders) ? deliveryOrders.map(order => {
                          const orderDate = order.date || order.createdAt;
                          const formattedDate = orderDate && !isNaN(new Date(orderDate).getTime()) 
                            ? new Date(orderDate).toLocaleDateString() 
                            : 'N/A';
                          
                          return {
                            value: order._id,
                            label: `${order.deliveryOrderId || order.doNumber} - ${order.clientName?.name || order.recipient?.name || order.recipient?.businessName || 'N/A'} (${formattedDate}) [${order.status || 'Pending'}]`
                          };
                        }) : []}
                        isSearchable
                        isClearable
                        isLoading={!Array.isArray(deliveryOrders) || deliveryOrders.length === 0}
                        loadingMessage={() => "Loading tiles delivery orders..."}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            minHeight: '42px',
                            backgroundColor: darkMode ? '#374151' : base.backgroundColor,
                            borderColor: state.isFocused ? '#4f46e5' : darkMode ? '#4b5563' : '#d1d5db',
                            borderWidth: '2px',
                            borderRadius: '12px',
                            boxShadow: state.isFocused ? '0 0 0 3px rgba(79, 70, 229, 0.1)' : 'none',
                            '&:hover': { 
                              borderColor: state.isFocused ? '#4f46e5' : darkMode ? '#6b7280' : '#9ca3af' 
                            }
                          }),
                          menu: (base) => ({
                            ...base,
                            backgroundColor: darkMode ? '#374151' : 'white',
                            borderColor: darkMode ? '#4b5563' : base.borderColor
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? (darkMode ? '#4b5563' : '#eef2ff') : (darkMode ? '#374151' : 'white'),
                            color: state.isSelected ? 'white' : darkMode ? '#e5e7eb' : '#374151',
                            '&:hover': {
                              backgroundColor: state.isSelected ? '#4f46e5' : darkMode ? '#4b5563' : '#eef2ff'
                            }
                          }),
                          placeholder: (base) => ({
                            ...base,
                            color: darkMode ? '#9ca3af' : '#9ca3af'
                          }),
                          singleValue: (base) => ({
                            ...base,
                            color: darkMode ? '#e5e7eb' : '#374151'
                          }),
                          input: (base) => ({
                            ...base,
                            color: darkMode ? '#e5e7eb' : '#374151'
                          })
                        }}
                      />
                      <div className="flex justify-between items-center mt-2">
                        {/* Status indicator for selected order */}
                        {selectedDeliveryOrder && (
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status:</span>
                            <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                              selectedDeliveryOrder.status === 'Completed' ? 'bg-green-100 text-green-800 border border-green-300' :
                              selectedDeliveryOrder.status === 'Cancelled' ? 'bg-red-100 text-red-800 border border-red-300' :
                              'bg-yellow-100 text-yellow-800 border border-yellow-300'
                            }`}>
                              {selectedDeliveryOrder.status || 'Pending'}
                            </span>

                          </div>
                        )}
                        
                        <button
                          type="button"
                          onClick={() => setShowAdvancedFiltersModal(true)}
                          className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1.5 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <FaFilter className="text-xs" />
                          Advanced Filters ({Array.isArray(filteredDeliveryOrders) ? filteredDeliveryOrders.length : 0} orders)
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Return Date *
                      </label>
                      <input
                        type="date"
                        className={`w-full px-3 py-2 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                        value={formData.date}
                        onChange={(e) => setFormData(prev => ({...prev, date: e.target.value}))}
                        required
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Remarks (Optional)
                      </label>
                      <textarea
                        rows={3}
                        className={`w-full px-3 py-2 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300'}`}
                        value={formData.remarks}
                        onChange={(e) => setFormData(prev => ({...prev, remarks: e.target.value}))}
                        placeholder="Enter any additional remarks about this return..."
                      />
                    </div>
                  </div>
                </div>

                {/* Return Items */}
                <div className={`border-2 p-6 rounded-xl shadow-sm transition-colors duration-300 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'}`}>
                  <h4 className={`font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className={`p-2 rounded-lg ${darkMode ? 'bg-indigo-900/50' : 'bg-gradient-to-br from-indigo-100 to-purple-100'}`}>
                      <FaBox className="text-indigo-600" />
                    </div>
                    Return Tiles
                    {selectedDeliveryOrder && <span className={`text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>from {selectedDeliveryOrder.doNumber || selectedDeliveryOrder.deliveryOrderId}</span>}
                  </h4>

                  {!selectedDeliveryOrder ? (
                    <div className={`text-center py-12 rounded-xl border-2 border-dashed ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                      <FaTruck className={`text-5xl mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                      <p className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Please select a tiles delivery order first</p>
                    </div>
                  ) : formData.items.length === 0 ? (
                    <div className={`text-center py-8 rounded-xl border-2 border-dashed ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                      <p className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No tile items in this delivery order</p>
                    </div>
                  ) : (
                    <div className="max-h-[500px] overflow-y-auto space-y-3">
                      {formData.items.map((item, index) => (
                        <div key={item.tileId} className={`border-2 p-4 rounded-xl transition-colors ${darkMode ? 'bg-gray-800 border-gray-600 hover:border-indigo-400' : 'bg-white border-gray-200 hover:border-indigo-300'}`}>
                          <div
                            className="flex justify-between items-center cursor-pointer"
                            onClick={() => toggleItemExpansion(item.tileId)}
                          >
                            <div>
                              <h5 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.tileName}</h5>
                              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {item.tileSize} | Available: {item.availableBoxes}B or {item.availableTotalPieces}P
                                {item.totalReturnedBoxes > 0 || item.totalReturnedPieces > 0 ? (
                                  <span className="text-orange-500 ml-2">(Previously returned: {item.totalReturnedBoxes}B, {item.totalReturnedPieces}P)</span>
                                ) : null}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {(item.returnTotalBoxes > 0 || item.returnTotalPieces > 0) && (
                                <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs font-bold rounded-lg">
                                  Returning: {item.returnTotalBoxes}B, {item.returnTotalPieces}P
                                </span>
                              )}
                              <div className={`p-2 rounded-lg ${darkMode ? 'bg-indigo-900/50' : 'bg-indigo-50'}`}>
                                {expandedItems[item.tileId] ? <FaChevronUp className="text-indigo-600" /> : <FaChevronDown className="text-indigo-600" />}
                              </div>
                            </div>
                          </div>

                          {expandedItems[item.tileId] && (
                            <div className="mt-4 space-y-4">
                              {/* Step 1: Total Return Quantity */}
                              <div className={`border-2 p-4 rounded-xl ${darkMode ? 'bg-indigo-900/20 border-indigo-700' : 'bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-200'}`}>
                                <label className={`block text-sm font-bold mb-3 ${darkMode ? 'text-indigo-300' : 'text-indigo-900'}`}>
                                  Step 1: How many to return? (Max: {item.availableBoxes}B or {item.availableTotalPieces}P)
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Return Boxes</label>
                                    <input
                                      type="number"
                                      placeholder="0"
                                      className={`w-full px-3 py-2 border-2 rounded-xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all ${darkMode ? 'border-gray-500 bg-gray-600 text-white' : 'border-indigo-300 focus:border-indigo-500'}`}
                                      value={item.returnTotalBoxes || ''}
                                      onChange={(e) => handleItemChange(index, 'returnTotalBoxes', e.target.value)}
                                      min="0"
                                      max={item.availableBoxes}
                                    />
                                  </div>
                                  <div>
                                    <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Return Pieces {item.returnTotalBoxes > 0 ? `(Max: ${item.maxReturnPieces})` : ''}</label>
                                    <input
                                      type="number"
                                      placeholder="0"
                                      className={`w-full px-3 py-2 border-2 rounded-xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all ${darkMode ? 'border-gray-500 bg-gray-600 text-white' : 'border-indigo-300 focus:border-indigo-500'}`}
                                      value={item.returnTotalPieces || ''}
                                      onChange={(e) => handleItemChange(index, 'returnTotalPieces', e.target.value)}
                                      min="0"
                                      max={item.maxReturnPieces ?? item.availableTotalPieces}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Step 2: Distribution */}
                              {(item.returnTotalBoxes > 0 || item.returnTotalPieces > 0) && (
                                <div className={`border-2 p-4 rounded-xl ${darkMode ? 'bg-gray-900/50 border-gray-600' : 'bg-gradient-to-br from-gray-50 to-white border-gray-200'}`}>
                                  <label className={`block text-sm font-bold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                    Step 2: Distribute returned stock into categories
                                  </label>
                                  <p className={`text-xs mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                    Fresh + Corner Damaged + Full Damaged must equal total. Adjust Corner/Full — Fresh auto-calculates.
                                  </p>

                                  {item.returnTotalBoxes > 0 && (
                                    <div className="mb-3">
                                      <div className={`text-xs font-bold uppercase tracking-wide mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Boxes Distribution (Total: {item.returnTotalBoxes})
                                      </div>
                                      <div className="grid grid-cols-3 gap-2">
                                        <div>
                                          <label className="block text-xs font-semibold mb-1 text-green-700">Fresh Boxes</label>
                                          <input type="number" className={`w-full px-3 py-2 border-2 rounded-xl text-sm ${darkMode ? 'border-green-700 bg-green-900/30 text-green-300' : 'border-green-300 bg-green-50 text-green-900'}`} value={item.returnedFreshBoxes || ''} readOnly />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-semibold mb-1 text-yellow-700">Corner Dmg Boxes</label>
                                          <input type="number" placeholder="0" className={`w-full px-3 py-2 border-2 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 ${darkMode ? 'border-yellow-700 bg-yellow-900/30 text-yellow-300' : 'border-yellow-300 bg-yellow-50'}`} value={item.returnedCornerDamagedBoxes || ''} onChange={(e) => handleItemChange(index, 'returnedCornerDamagedBoxes', e.target.value)} min="0" max={item.returnTotalBoxes} />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-semibold mb-1 text-red-700">Full Dmg Boxes</label>
                                          <input type="number" placeholder="0" className={`w-full px-3 py-2 border-2 rounded-xl text-sm focus:ring-2 focus:ring-red-500 ${darkMode ? 'border-red-700 bg-red-900/30 text-red-300' : 'border-red-300 bg-red-50'}`} value={item.returnedFullDamagedBoxes || ''} onChange={(e) => handleItemChange(index, 'returnedFullDamagedBoxes', e.target.value)} min="0" max={item.returnTotalBoxes} />
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {item.returnTotalPieces > 0 && (
                                    <div>
                                      <div className={`text-xs font-bold uppercase tracking-wide mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Pieces Distribution (Total: {item.returnTotalPieces})
                                      </div>
                                      <div className="grid grid-cols-3 gap-2">
                                        <div>
                                          <label className="block text-xs font-semibold mb-1 text-green-700">Fresh Pieces</label>
                                          <input type="number" className={`w-full px-3 py-2 border-2 rounded-xl text-sm ${darkMode ? 'border-green-700 bg-green-900/30 text-green-300' : 'border-green-300 bg-green-50 text-green-900'}`} value={item.returnedFreshPieces || ''} readOnly />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-semibold mb-1 text-yellow-700">Corner Dmg Pieces</label>
                                          <input type="number" placeholder="0" className={`w-full px-3 py-2 border-2 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 ${darkMode ? 'border-yellow-700 bg-yellow-900/30 text-yellow-300' : 'border-yellow-300 bg-yellow-50'}`} value={item.returnedCornerDamagedPieces || ''} onChange={(e) => handleItemChange(index, 'returnedCornerDamagedPieces', e.target.value)} min="0" max={item.returnTotalPieces} />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-semibold mb-1 text-red-700">Full Dmg Pieces</label>
                                          <input type="number" placeholder="0" className={`w-full px-3 py-2 border-2 rounded-xl text-sm focus:ring-2 focus:ring-red-500 ${darkMode ? 'border-red-700 bg-red-900/30 text-red-300' : 'border-red-300 bg-red-50'}`} value={item.returnedFullDamagedPieces || ''} onChange={(e) => handleItemChange(index, 'returnedFullDamagedPieces', e.target.value)} min="0" max={item.returnTotalPieces} />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Stock Items Section */}
                  {selectedDeliveryOrder && formData.stockItems.length > 0 && (
                    <div className="mt-6">
                      <h4 className={`font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-900/50' : 'bg-gradient-to-br from-purple-100 to-pink-100'}`}>
                          <FaBoxOpen className="text-purple-600" />
                        </div>
                        Return Stock Items
                      </h4>
                      <div className="space-y-3">
                        {formData.stockItems.map((si, siIndex) => (
                          <div key={si.stockItemId} className={`border-2 p-4 rounded-xl ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                            <div className="flex justify-between items-center mb-3">
                              <div>
                                <h5 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{si.stockItemName}</h5>
                                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {si.stockItemCode} | {si.unitOfMeasure} | Available: {si.availableQuantity}
                                  {si.totalReturnedQuantity > 0 && <span className="text-orange-500 ml-2">(Previously returned: {si.totalReturnedQuantity})</span>}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>Total Return Qty</label>
                                <input type="number" placeholder="0" className={`w-full px-3 py-2 border-2 rounded-xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 ${darkMode ? 'border-gray-500 bg-gray-600 text-white' : 'border-indigo-300'}`} value={si.returnTotalQuantity || ''} onChange={(e) => handleStockItemChange(siIndex, 'returnTotalQuantity', e.target.value)} min="0" max={si.availableQuantity} />
                              </div>
                              {si.returnTotalQuantity > 0 && (
                                <>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1 text-green-700">Fresh Qty</label>
                                    <input type="number" className={`w-full px-3 py-2 border-2 rounded-xl text-sm ${darkMode ? 'border-green-700 bg-green-900/30 text-green-300' : 'border-green-300 bg-green-50 text-green-900'}`} value={si.returnedFreshQuantity || ''} readOnly />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1 text-red-700">Damaged Qty</label>
                                    <input type="number" placeholder="0" className={`w-full px-3 py-2 border-2 rounded-xl text-sm focus:ring-2 focus:ring-red-500 ${darkMode ? 'border-red-700 bg-red-900/30 text-red-300' : 'border-red-300 bg-red-50'}`} value={si.returnedDamagedQuantity || ''} onChange={(e) => handleStockItemChange(siIndex, 'returnedDamagedQuantity', e.target.value)} min="0" max={si.returnTotalQuantity} />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`border-t-2 p-6 rounded-b-2xl ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex justify-end gap-3">
                <button
                  onClick={async () => {
                    if (formData.originalDeliveryOrderId || formData.remarks) {
                      const result = await Swal.fire({
                        title: 'Discard Changes?',
                        text: 'Are you sure you want to close without saving? All changes will be lost.',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#ef4444',
                        cancelButtonColor: '#6b7280',
                        confirmButtonText: 'Yes, Discard',
                        cancelButtonText: 'Continue Editing'
                      });
                      if (result.isConfirmed) {
                        setShowCreateModal(false);
                        resetForm();
                      }
                    } else {
                      setShowCreateModal(false);
                      resetForm();
                    }
                  }}
                  className="px-6 py-2.5 bg-gradient-to-r from-gray-400 to-gray-500 text-white font-semibold rounded-xl hover:from-gray-500 hover:to-gray-600 transition-all shadow-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateReturn}
                  disabled={!formData.originalDeliveryOrderId || loading}
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg transition-all"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle />
                      Create Return
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Return Modal */}
      {showViewModal && selectedReturn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className={`relative rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                    <FaEye className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Return Details</h3>
                    <p className="text-white/90 text-sm mt-1">{selectedReturn.returnId}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`border-2 p-6 rounded-xl ${darkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'}`}>
                  <h4 className={`font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                    <div className="w-1.5 h-6 bg-gradient-to-b from-blue-600 to-indigo-600 rounded-full"></div>
                    Return Information
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <strong className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Return ID:</strong> 
                      <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedReturn.returnId}</span>
                    </div>
                    <div className="flex justify-between">
                      <strong className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date:</strong> 
                      <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{new Date(selectedReturn.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <strong className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status:</strong> 
                      <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getStatusColor(selectedReturn.status)}`}>
                        {selectedReturn.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <strong className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Created By:</strong> 
                      <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedReturn.createdBy?.name}</span>
                    </div>
                    {selectedReturn.remarks && (
                      <div className={`pt-2 border-t-2 ${darkMode ? 'border-blue-700' : 'border-blue-200'}`}>
                        <strong className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Remarks:</strong>
                        <p className={`mt-1 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedReturn.remarks}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className={`border-2 p-6 rounded-xl ${darkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200'}`}>
                  <h4 className={`font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-purple-300' : 'text-purple-900'}`}>
                    <div className="w-1.5 h-6 bg-gradient-to-b from-purple-600 to-pink-600 rounded-full"></div>
                    Tiles Delivery Order Info
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <strong className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Order ID:</strong> 
                      <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedReturn.originalDeliveryOrderId?.deliveryOrderId}</span>
                    </div>
                    <div className="flex justify-between">
                      <strong className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Client:</strong> 
                      <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedReturn.originalDeliveryOrderId?.clientName?.name || selectedReturn.originalDeliveryOrderId?.recipient?.name || selectedReturn.originalDeliveryOrderId?.recipient?.businessName || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Returned Tile Items */}
              {selectedReturn.items && selectedReturn.items.length > 0 && (
                <div className={`mt-6 border-2 p-6 rounded-xl ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'}`}>
                  <h4 className={`font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="w-1.5 h-6 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full"></div>
                    Returned Tile Items
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${darkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-800'}`}>{selectedReturn.items.length}</span>
                  </h4>
                  <div className="space-y-3">
                    {selectedReturn.items.map((item, idx) => (
                      <div key={idx} className={`border-2 p-4 rounded-xl ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h5 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.tileId?.name || 'Unknown Tile'}</h5>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {item.tileId?.tileId} | {item.tileId?.size?.length}x{item.tileId?.size?.width} {item.tileId?.size?.unit}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-lg text-sm font-bold ${darkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-800'}`}>
                            Total: {item.totalReturnedBoxes || 0}B, {item.totalReturnedPieces || 0}P
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className={`p-3 rounded-lg border ${darkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'}`}>
                            <div className={`text-xs font-bold uppercase ${darkMode ? 'text-green-400' : 'text-green-700'}`}>Fresh</div>
                            <div className={`text-lg font-bold ${darkMode ? 'text-green-300' : 'text-green-900'}`}>{item.returnedFreshBoxes || 0}B, {item.returnedFreshPieces || 0}P</div>
                          </div>
                          <div className={`p-3 rounded-lg border ${darkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'}`}>
                            <div className={`text-xs font-bold uppercase ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>Corner Damaged</div>
                            <div className={`text-lg font-bold ${darkMode ? 'text-yellow-300' : 'text-yellow-900'}`}>{item.returnedCornerDamagedBoxes || 0}B, {item.returnedCornerDamagedPieces || 0}P</div>
                          </div>
                          <div className={`p-3 rounded-lg border ${darkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'}`}>
                            <div className={`text-xs font-bold uppercase ${darkMode ? 'text-red-400' : 'text-red-700'}`}>Full Damaged</div>
                            <div className={`text-lg font-bold ${darkMode ? 'text-red-300' : 'text-red-900'}`}>{item.returnedFullDamagedBoxes || 0}B, {item.returnedFullDamagedPieces || 0}P</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Returned Stock Items */}
              {selectedReturn.stockItems && selectedReturn.stockItems.length > 0 && (
                <div className={`mt-6 border-2 p-6 rounded-xl ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200'}`}>
                  <h4 className={`font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="w-1.5 h-6 bg-gradient-to-b from-purple-600 to-pink-600 rounded-full"></div>
                    Returned Stock Items
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>{selectedReturn.stockItems.length}</span>
                  </h4>
                  <div className="space-y-3">
                    {selectedReturn.stockItems.map((si, idx) => (
                      <div key={idx} className={`border-2 p-4 rounded-xl ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h5 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{si.stockItemId?.name || 'Unknown Item'}</h5>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {si.stockItemId?.stockId} | {si.stockItemId?.unitOfMeasure}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-lg text-sm font-bold ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                            Total: {si.totalReturnedQuantity || 0}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className={`p-3 rounded-lg border ${darkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'}`}>
                            <div className={`text-xs font-bold uppercase ${darkMode ? 'text-green-400' : 'text-green-700'}`}>Fresh</div>
                            <div className={`text-lg font-bold ${darkMode ? 'text-green-300' : 'text-green-900'}`}>{si.returnedFreshQuantity || 0}</div>
                          </div>
                          <div className={`p-3 rounded-lg border ${darkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'}`}>
                            <div className={`text-xs font-bold uppercase ${darkMode ? 'text-red-400' : 'text-red-700'}`}>Damaged</div>
                            <div className={`text-lg font-bold ${darkMode ? 'text-red-300' : 'text-red-900'}`}>{si.returnedDamagedQuantity || 0}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-6 py-2.5 bg-gradient-to-r from-gray-400 to-gray-500 text-white font-semibold rounded-xl hover:from-gray-500 hover:to-gray-600 transition-all shadow-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}




      {/* Advanced Filters Modal */}
      {showAdvancedFiltersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className={`relative rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                    <FaCog className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Advanced Filters</h3>
                    <p className="text-white/90 text-sm mt-1">Filter Tiles Delivery Orders</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAdvancedFiltersModal(false)}
                  className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Client Name
                    </label>
                    <input
                      type="text"
                      placeholder="Search by client name..."
                      className={`w-full px-3 py-2 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                      value={advancedFilters.clientName}
                      onChange={(e) => handleAdvancedFilterChange('clientName', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Delivery Order ID
                    </label>
                    <input
                      type="text"
                      placeholder="Search by order ID..."
                      className={`w-full px-3 py-2 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                      value={advancedFilters.deliveryOrderId}
                      onChange={(e) => handleAdvancedFilterChange('deliveryOrderId', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Status
                    </label>
                    <select
                      className={`w-full px-3 py-2 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                      value={advancedFilters.status}
                      onChange={(e) => handleAdvancedFilterChange('status', e.target.value)}
                    >
                      <option value="">All Status</option>
                      <option value="delivered">Delivered</option>
                      <option value="pending">Pending</option>
                      <option value="in-transit">In Transit</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Location
                    </label>
                    <select
                      className={`w-full px-3 py-2 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                      value={advancedFilters.location}
                      onChange={(e) => handleAdvancedFilterChange('location', e.target.value)}
                    >
                      <option value="">All Locations</option>
                      {locations.map(location => (
                        <option key={location._id} value={location._id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Date From
                    </label>
                    <input
                      type="date"
                      className={`w-full px-3 py-2 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                      value={advancedFilters.dateFrom}
                      onChange={(e) => handleAdvancedFilterChange('dateFrom', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Date To
                    </label>
                    <input
                      type="date"
                      className={`w-full px-3 py-2 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                      value={advancedFilters.dateTo}
                      onChange={(e) => handleAdvancedFilterChange('dateTo', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Minimum Amount
                    </label>
                    <input
                      type="number"
                      placeholder="0.00"
                      className={`w-full px-3 py-2 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                      value={advancedFilters.minAmount}
                      onChange={(e) => handleAdvancedFilterChange('minAmount', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Maximum Amount
                    </label>
                    <input
                      type="number"
                      placeholder="0.00"
                      className={`w-full px-3 py-2 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                      value={advancedFilters.maxAmount}
                      onChange={(e) => handleAdvancedFilterChange('maxAmount', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Filter Results Summary */}
              <div className={`mt-6 p-4 border-2 rounded-xl ${darkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                      <FaInfoCircle className={`${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <span className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                      <strong className="font-bold">{Array.isArray(filteredDeliveryOrders) ? filteredDeliveryOrders.length : 0}</strong> delivery orders match your filters
                      {Array.isArray(allDeliveryOrders) && allDeliveryOrders.length > 0 && (
                        <span className="ml-1">out of {allDeliveryOrders.length} total</span>
                      )}
                    </span>
                  </div>
                  <button
                    onClick={resetAdvancedFilters}
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-100 hover:bg-indigo-200 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`border-t-2 p-6 rounded-b-2xl ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowAdvancedFiltersModal(false)}
                  className="px-6 py-2.5 bg-gradient-to-r from-gray-400 to-gray-500 text-white font-semibold rounded-xl hover:from-gray-500 hover:to-gray-600 transition-all shadow-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    applyAdvancedFilters();
                    setShowAdvancedFiltersModal(false);
                    Swal.fire({
                      icon: 'success',
                      title: 'Filters Applied',
                      text: `${Array.isArray(filteredDeliveryOrders) ? filteredDeliveryOrders.length : 0} delivery orders found`,
                      toast: true,
                      position: 'top-end',
                      showConfirmButton: false,
                      timer: 2000
                    });
                  }}
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:from-indigo-700 hover:to-purple-700 flex items-center gap-2 shadow-lg transition-all"
                >
                  <FaFilter />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TilesReturn;